import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { changesApi } from '../../services/changesApi';
import type { ChangeDetailTabDefinition, ChangeFormField } from '../../types/changes';
import { Card, Tab, Tabs } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ChangeApprovalPlanCard from './components/ChangeApprovalPlanCard';
import ChangeEmergencyApprovalCard from './components/ChangeEmergencyApprovalCard';
import ChangeApprovalsTab from './components/ChangeApprovalsTab';
import ChangeTransportTab from './components/ChangeTransportTab';
import ChangeEditForm from './components/ChangeEditForm';
import ChangeHeader from './components/ChangeHeader';
import ChangeImplantationForm from './components/ChangeImplantationForm';
import ChangeStatusPanel from './components/ChangeStatusPanel';
import ChangeSubtasksTab from './components/ChangeSubtasksTab';
import ChangeDetailTabEntriesPanel from './components/ChangeDetailTabEntriesPanel';
import ChangeEvidenceTab from './components/ChangeEvidenceTab';
import ChangeAssistantTab from './components/ChangeAssistantTab';
import {
  approvalsBadgeCount,
  canEditApprovalPlan,
  canEditApprovalPlanByStatus,
  canViewApprovalPlan,
  canVoidApprovals,
} from './changeApprovalPlan';
import {
  canViewPostImplementation,
  isEmergencyChangeType,
  POST_IMPL_STATUSES,
} from './changeLabels';
import { listStatusWaitMissing } from './changeStatusWait';
import { useChangeDetail } from './hooks/useChangeDetail';
import { canEditChangeCabDeck } from './changePermissions';
import {
  customDetailTabIdFromKey,
  isCustomDetailTabKey,
  normalizePageTabOrder,
  visiblePageTabOrder,
} from './changeDetailPageTabLayout';

const ChangeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const changeId = Number(id);
  const { user } = useAuth();
  const { change, allowedTransitions, blockedTransitions, loading, error, reload } =
    useChangeDetail(changeId);
  const [activeTab, setActiveTab] = useState('subtasks');
  const [summaryLayout, setSummaryLayout] = useState<ChangeFormField[]>([]);
  const [detailTabs, setDetailTabs] = useState<ChangeDetailTabDefinition[]>([]);
  const [pageTabOrder, setPageTabOrder] = useState<string[]>([]);

  const reloadSummaryLayout = useCallback(() => {
    void changesApi
      .getDetailSummarySchema()
      .then((data) => setSummaryLayout(data.items))
      .catch(() => setSummaryLayout([]));
  }, []);

  useEffect(() => {
    reloadSummaryLayout();
  }, [reloadSummaryLayout]);

  useEffect(() => {
    if (!change?.id) {
      setDetailTabs([]);
      setPageTabOrder([]);
      return;
    }
    void changesApi
      .listVisibleDetailTabs(change.id, change.status)
      .then((data) => {
        setDetailTabs(data.items);
        setPageTabOrder(normalizePageTabOrder(data.page_tab_order));
      })
      .catch(() => {
        setDetailTabs([]);
        setPageTabOrder([]);
      });
  }, [change?.id, change?.status]);

  useEffect(() => {
    const onSaved = () => reloadSummaryLayout();
    window.addEventListener('change-detail-summary-saved', onSaved);
    return () => window.removeEventListener('change-detail-summary-saved', onSaved);
  }, [reloadSummaryLayout]);

  const pendingCount = useMemo(
    () =>
      (change?.approvals || []).filter(
        (a) => {
          const track = (a.track || '').toLowerCase();
          if (track === 'transporte' || track === 'emergencial') return false;
          return (
            a.status === 'pendente' && (Boolean(a.group_id) || a.track === 'gestao_mudancas')
          );
        },
      ).length,
    [change],
  );
  const decidedCount = useMemo(
    () =>
      (change?.approvals || []).filter(
        (a) => {
          const track = (a.track || '').toLowerCase();
          if (track === 'transporte' || track === 'emergencial') return false;
          return (
            a.status !== 'pendente' && (Boolean(a.group_id) || a.track === 'gestao_mudancas')
          );
        },
      ).length,
    [change],
  );
  const isEmergency = Boolean(change && isEmergencyChangeType(change.change_type));
  const emergencyStepsCount = change?.emergency_approval?.steps?.length ?? 0;
  const approvalsCount = useMemo(() => {
    if (isEmergency) {
      // Emergencial: comitê não existe; gestores decidem na aba Transporte.
      return 0;
    }
    return approvalsBadgeCount(
      change?.approval_plan,
      pendingCount,
      change?.status,
      decidedCount,
      change?.approvals,
    );
  }, [change, decidedCount, isEmergency, pendingCount]);
  const canViewPostImpl = Boolean(change && canViewPostImplementation(change));
  const showPostImpl = Boolean(
    change && POST_IMPL_STATUSES.has(change.status) && canViewPostImpl,
  );
  const canEditCabDeck = canEditChangeCabDeck(user, change ?? undefined);
  const statusAllowsPlanEdit = Boolean(
    change && canEditApprovalPlanByStatus(change.status, change.approvals),
  );
  const canEditPlan = Boolean(
    change && statusAllowsPlanEdit && canEditApprovalPlan(change, user, change.approval_settings),
  );
  const canVoidCommitteePlan = Boolean(
    change && canVoidApprovals(change, user, change.approval_settings, 'committee'),
  );
  const canVoidTransportPlan = Boolean(
    change && canVoidApprovals(change, user, change.approval_settings, 'transport'),
  );
  const missingWaitItems = useMemo(() => listStatusWaitMissing(change), [change]);
  const transportApprovalsCount = useMemo(
    () => (change?.approvals || []).filter((slot) => slot.track === 'transporte').length,
    [change?.approvals],
  );
  const gateSubtasksCount = useMemo(
    () => (change?.subtasks || []).filter((item) => item.gates_implementar).length,
    [change?.subtasks],
  );
  const transportTabBadgeCount = transportApprovalsCount + gateSubtasksCount;
  const emergencyTransportPendingCount = useMemo(
    () =>
      (change?.approvals || []).filter(
        (slot) => slot.track === 'transporte' && slot.status === 'pendente',
      ).length,
    [change?.approvals],
  );
  const emergencyShowsTransportPreview = Boolean(
    isEmergency &&
      change &&
      (change.status === 'avaliar' ||
        change.status === 'agendado' ||
        change.status === 'autorizar') &&
      emergencyStepsCount > 0,
  );
  /** Transporte: pendências primeiro; senão total de slots/gates (histórico); emergencial cedo = preview do cadastro. */
  const transportTabBadgeForTitle = useMemo(() => {
    const pendingTransport = emergencyTransportPendingCount;
    const totalTransport = transportApprovalsCount + gateSubtasksCount;
    const pendingAndGates = pendingTransport + gateSubtasksCount;
    if (isEmergency) {
      if (pendingAndGates > 0) return pendingAndGates;
      if (totalTransport > 0) return totalTransport;
      if (emergencyShowsTransportPreview && emergencyStepsCount > 0) return emergencyStepsCount;
      return 0;
    }
    return totalTransport;
  }, [
    emergencyShowsTransportPreview,
    emergencyStepsCount,
    emergencyTransportPendingCount,
    gateSubtasksCount,
    isEmergency,
    transportApprovalsCount,
  ]);
  const showTransportTab = useMemo(() => {
    if (!change) return false;
    if (isEmergency) {
      if (change.status === 'transporte') return true;
      if (change.status === 'implementar' || change.status === 'revisar') {
        return gateSubtasksCount > 0 || change.status === 'implementar';
      }
      if (transportApprovalsCount > 0) return true;
      if (
        (change.status === 'avaliar' ||
          change.status === 'agendado' ||
          change.status === 'autorizar') &&
        emergencyStepsCount > 0
      ) {
        return true;
      }
      return false;
    }
    if (transportTabBadgeCount > 0) return true;
    return change.status === 'transporte' || change.status === 'implementar';
  }, [
    change,
    emergencyStepsCount,
    gateSubtasksCount,
    isEmergency,
    transportApprovalsCount,
    transportTabBadgeCount,
  ]);
  const canViewPlan = Boolean(
    change && canViewApprovalPlan(change, user, change.approval_settings),
  );
  const showApprovalPlanCard = Boolean(
    canViewPlan &&
      change &&
      change.status !== 'revisar' &&
      change.status !== 'encerrar',
  );
  const customTabById = useMemo(
    () => new Map(detailTabs.map((tab) => [tab.id, tab])),
    [detailTabs],
  );
  const detailTabKeysToRender = useMemo(
    () =>
      visiblePageTabOrder(normalizePageTabOrder(pageTabOrder), {
        showTransportTab,
      }),
    [pageTabOrder, showTransportTab],
  );

  const renderOrderedDetailTab = (key: string): React.ReactNode => {
    if (key === 'subtasks') {
      return (
        <Tab eventKey="subtasks" title={`Subtarefas (${change?.subtasks?.length || 0})`}>
          {activeTab === 'subtasks' ? (
            <ChangeSubtasksTab change={change!} onUpdated={reload} />
          ) : null}
        </Tab>
      );
    }
    if (key === 'approvals') {
      return (
        <Tab eventKey="approvals" title={`Aprovações (${approvalsCount})`}>
          {activeTab === 'approvals' ? (
            <ChangeApprovalsTab
              change={change!}
              canVoidApprovals={canVoidCommitteePlan}
              approvalScope="committee"
              onUpdated={reload}
            />
          ) : null}
        </Tab>
      );
    }
    if (key === 'transport' && showTransportTab) {
      return (
        <Tab eventKey="transport" title={`Transporte (${transportTabBadgeForTitle})`}>
          {activeTab === 'transport' ? (
            <ChangeTransportTab
              change={change!}
              canVoidApprovals={canVoidTransportPlan}
              onUpdated={reload}
            />
          ) : null}
        </Tab>
      );
    }
    if (key === 'attachments') {
      return (
        <Tab eventKey="attachments" title="Anexos">
          {activeTab === 'attachments' ? (
            <ChangeEvidenceTab
              changeId={change!.id}
              active
              canUpload={change!.status !== 'cancelada'}
            />
          ) : null}
        </Tab>
      );
    }
    if (key === 'assistant') {
      return (
        <Tab eventKey="assistant" title="Resumo IA">
          <ChangeAssistantTab changeId={change!.id} active={activeTab === 'assistant'} />
        </Tab>
      );
    }
    if (isCustomDetailTabKey(key)) {
      const tabId = customDetailTabIdFromKey(key);
      const tab = tabId ? customTabById.get(tabId) : undefined;
      if (!tab) return null;
      return (
        <Tab
          key={key}
          eventKey={key}
          title={`${tab.label} (${tab.entries_count ?? 0})`}
        >
          {activeTab === key ? (
            <ChangeDetailTabEntriesPanel changeId={change!.id} tab={tab} active />
          ) : null}
        </Tab>
      );
    }
    return null;
  };

  if (loading && !change) {
    return <div className="container-fluid py-3">Carregando mudança...</div>;
  }

  if (!change) {
    return (
      <div className="container-fluid py-3">
        <Card>
          <Card.Body className="d-flex flex-wrap align-items-center gap-2">
            <span>{error || 'Mudança não encontrada.'}</span>
            {error ? (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => void reload()}>
                Tentar de novo
              </button>
            ) : null}
            <Link to="/changes" className="btn btn-sm btn-outline-secondary">
              Voltar para listagem
            </Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
        <ChangeStatusPanel
          changeId={change.id}
          currentStatus={change.status}
          allowed={allowedTransitions}
          blocked={blockedTransitions}
          missingItems={missingWaitItems}
          canMutateChange={Boolean(change.can_mutate_change)}
          onUpdated={reload}
        />
      </div>
      <ChangeHeader
        change={change}
        summaryLayout={summaryLayout}
        showPostImplementation={canViewPostImpl}
        cabDeckHref={
          canEditCabDeck && change ? `/changes/${change.id}/apresentacao` : undefined
        }
      />
      <ChangeEditForm change={change} onUpdated={reload} />
      {showApprovalPlanCard ? (
        isEmergencyChangeType(change.change_type) ? (
          <ChangeEmergencyApprovalCard preview={change.emergency_approval} />
        ) : (
          <ChangeApprovalPlanCard
            changeId={change.id}
            changeStatus={change.status}
            approvals={change.approvals}
            plan={change.approval_plan}
            settings={change.approval_settings}
            canEdit={canEditPlan}
            editLockedReason={
              statusAllowsPlanEdit && !canEditPlan
                ? 'Você não tem permissão para montar o plano de aprovação desta mudança (cadastro de grupos de aprovação).'
                : undefined
            }
            onUpdated={reload}
          />
        )
      ) : null}
      {showPostImpl ? <ChangeImplantationForm change={change} onUpdated={reload} /> : null}

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || 'subtasks')}
        id="changes-detail-tabs"
        className="mb-3"
      >
        {detailTabKeysToRender.map((key) => renderOrderedDetailTab(key))}
      </Tabs>
    </div>
  );
};

export default ChangeDetailPage;
